/**
 * DesktopSidebar — left rail mounted on desktop only.
 *
 * Layout math (the comment in the brief):
 *   - The centered card on desktop is 540px wide and offset 140px right of
 *     center: `left: calc(50% + 140px)` → card spans
 *     `[50% - 130px, 50% + 410px]`.
 *   - This sidebar's right edge sits flush with the card's left edge:
 *     `right edge = 50% - 130px`.
 *   - Sidebar is 280px wide, so its left edge:
 *     `50% - 130 - 280 = 50% - 410px`.
 *   - That's where `position: fixed; left: calc(50% - 410px)` comes from.
 *
 * Mounts inside `<App />` once at the top level — `null` on tablet/mobile
 * so it doesn't bloat the initial render or steal taps. The `useGetMatches`
 * call is deduped by react-query against the BottomNav's already-running
 * query, so we don't double-fetch.
 *
 * Track 5 (foundation files) owns this component.
 */
import { memo, useMemo, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useGetMatches } from '@workspace/api-client-react';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { colors, typography, space, radius, zIndex } from '@/lib/designTokens';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/lib/auth';
import { PapacitoLogo } from '@/components/PapacitoLogo';
import { optimizedImg } from '@/lib/utils';
import { isActiveNow } from '@/lib/lastActive';
import { buildWhopCheckoutUrlWithTracking, getCheckoutBaseUrl } from '@/lib/checkoutUrl';

type NavItem = {
  id: string;
  label: string;
  route: string;
  icon: (active: boolean) => ReactNode;
};

function activeColor(active: boolean): string {
  return active ? colors.accent : colors.textSecondary;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'swipe',
    label: 'Swipe',
    route: '/discover',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? colors.accent : 'none'} stroke={activeColor(active)} strokeWidth="2" strokeLinecap="round">
        <path d="M12 2C12 2 7 8 7 13a5 5 0 0010 0c0-2-1-4-2-5.5C14 9 13 11 13 13a1 1 0 01-2 0c0-3 1.5-5.5 1-9z" />
      </svg>
    ),
  },
  {
    id: 'explore',
    label: 'Explore',
    route: '/explore',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeColor(active)} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill={activeColor(active)} stroke="none" />
      </svg>
    ),
  },
  {
    id: 'likes',
    label: 'Likes',
    route: '/likes',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? colors.accent : 'none'} stroke={activeColor(active)} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
      </svg>
    ),
  },
  {
    id: 'chat',
    label: 'Chat',
    route: '/matches',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={active ? colors.accent : 'none'} stroke={activeColor(active)} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    ),
  },
  {
    id: 'profile',
    label: 'Profile',
    route: '/profile',
    icon: (active) => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeColor(active)} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
];

type RecentMatch = {
  matchId: number;
  name: string;
  photoUrl: string | null;
  lastActiveAt: string | null;
};

export const DesktopSidebar = memo(function DesktopSidebar() {
  const breakpoint = useBreakpoint();
  const [location, setLocation] = useLocation();
  const { token, user } = useAuth();
  const { isSubscriber } = useSubscription();

  // The query is shared with BottomNav / Matches list — react-query dedupes
  // on the query key so this doesn't double-fetch. The component returns
  // null below when there's no token / not desktop, but the hook still has
  // to run unconditionally to satisfy the Rules of Hooks.
  const { data: matchesRaw = [] } = useGetMatches();

  const recent: RecentMatch[] = useMemo(() => {
    if (!matchesRaw || matchesRaw.length === 0) return [];
    const sorted = [...matchesRaw].sort((a, b) => {
      const aTime = (a as any).lastMessageAt
        ? new Date((a as any).lastMessageAt).getTime()
        : new Date((a as any).createdAt || 0).getTime();
      const bTime = (b as any).lastMessageAt
        ? new Date((b as any).lastMessageAt).getTime()
        : new Date((b as any).createdAt || 0).getTime();
      return bTime - aTime;
    });
    return sorted.slice(0, 5).map((m: any) => {
      const u = m.user || {};
      const photoUrl = u.photos?.[0]?.url
        ? optimizedImg(u.photos[0].url, { width: 80, height: 80, quality: 80 })
        : null;
      return {
        matchId: m.matchId,
        name: (m.isVisitorCampaignLocked ? "Someone's in town" : u.name) || 'Match',
        photoUrl,
        lastActiveAt: u.lastActiveAt ?? null,
      };
    });
  }, [matchesRaw]);

  // Hide on tablet + mobile. Also hide if there's no auth token — sidebar is
  // app-shell only, not for landing/login routes.
  if (breakpoint !== 'desktop') return null;
  if (!token || !user) return null;

  // Don't render on routes where the chrome shouldn't appear (admin,
  // operator, onboarding, legal). Mirrors the BannerAdWrapper hidden list.
  const HIDE_ON: string[] = [
    '/login',
    '/register',
    '/admin',
    '/operator',
    '/onboarding',
    '/survey',
    '/vip-offer',
    '/privacy',
    '/terms',
    '/delete-account',
    '/verify-email',
    '/forgot-password',
    '/reset-password',
    '/install-prompt',
    '/push-prompt',
    '/install-app',
    '/subscription-success',
  ];
  if (HIDE_ON.some((p) => location === p || location.startsWith(p + '/'))) return null;

  const isActive = (route: string): boolean => {
    if (route === '/discover') return location === '/discover' || location === '/';
    if (route === '/matches') return location === '/matches' || location.startsWith('/chat');
    if (route === '/explore') return location === '/explore' || location.startsWith('/explore/');
    return location === route;
  };

  const handleSubscribe = () => {
    try {
      const ab = getCheckoutBaseUrl((user as any)?.id);
      const url = buildWhopCheckoutUrlWithTracking({
        baseUrl: ab.baseUrl,
        userId: (user as any)?.id,
        email: (user as any)?.email,
        source: 'sidebar_vip',
      });
      window.location.assign(url);
    } catch {
      // No-op — every code path above has its own fallback inside the helper.
    }
  };

  return (
    <aside
      data-testid="desktop-sidebar"
      style={{
        position: 'fixed',
        // See math comment at top of file.
        left: 'calc(50% - 410px)',
        top: 0,
        bottom: 0,
        width: 280,
        background: 'rgba(255,255,255,0.02)',
        borderRight: `1px solid ${colors.border}`,
        zIndex: zIndex.sidebar,
        display: 'flex',
        flexDirection: 'column',
        padding: `${space.lg}px`,
        boxSizing: 'border-box',
        overflowY: 'auto',
        // Pull above the OS scrollbar gutter on Windows/Linux without
        // breaking macOS overlay scrollbars.
        scrollbarWidth: 'thin',
      }}
    >
      {/* Logo */}
      <div style={{ marginBottom: space.xxl, paddingLeft: space.xs, paddingTop: space.xs }}>
        <PapacitoLogo size="medium" centered={false} />
      </div>

      {/* Vertical nav */}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.route);
          return (
            <button
              key={item.id}
              onClick={() => setLocation(item.route)}
              data-testid={`sidebar-nav-${item.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: space.md,
                height: 48,
                padding: `0 ${space.md}px`,
                background: active ? colors.surface : 'transparent',
                border: 'none',
                borderRadius: radius.md,
                color: active ? colors.accent : colors.textSecondary,
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
                transition: 'background 0.15s ease, color 0.15s ease',
                fontSize: 15,
                fontWeight: active ? 700 : 500,
                fontFamily: 'inherit',
                WebkitTapHighlightColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!active) (e.currentTarget as HTMLButtonElement).style.background = colors.surface;
              }}
              onMouseLeave={(e) => {
                if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <span style={{ display: 'inline-flex', width: 24, justifyContent: 'center' }}>
                {item.icon(active)}
              </span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Recent matches */}
      {recent.length > 0 && (
        <div style={{ marginTop: space.xxl, display: 'flex', flexDirection: 'column', gap: space.md }}>
          <h3
            style={{
              ...typography.h3,
              color: colors.textPrimary,
              margin: 0,
              padding: `0 ${space.md}px`,
              letterSpacing: 0.2,
            }}
          >
            Recent Matches
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {recent.map((m) => {
              const online = isActiveNow(m.lastActiveAt);
              return (
                <button
                  key={m.matchId}
                  onClick={() => setLocation(`/chat/${m.matchId}`)}
                  data-testid={`sidebar-match-${m.matchId}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: space.md,
                    padding: `${space.sm}px ${space.md}px`,
                    background: 'transparent',
                    border: 'none',
                    borderRadius: radius.md,
                    color: colors.textPrimary,
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    transition: 'background 0.15s ease',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = colors.surface;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      background: colors.surface2,
                      flexShrink: 0,
                    }}
                  >
                    {m.photoUrl ? (
                      <img
                        src={m.photoUrl}
                        alt={m.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        loading="lazy"
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: colors.textMuted,
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        {m.name.slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    {online && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: colors.success,
                          border: `2px solid #0a0a0a`,
                        }}
                      />
                    )}
                  </div>
                  <span
                    style={{
                      ...typography.body,
                      color: colors.textPrimary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                    }}
                  >
                    {m.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Spacer pushes the VIP CTA to the bottom of the rail */}
      <div style={{ flex: 1, minHeight: space.xxl }} />

      {/* VIP CTA — only render when user isn't already a subscriber */}
      {!isSubscriber && (
        <div
          style={{
            background: 'linear-gradient(135deg, #e11d48 0%, #ec4899 100%)',
            borderRadius: radius.lg,
            padding: space.lg,
            display: 'flex',
            flexDirection: 'column',
            gap: space.sm,
            color: '#fff',
            marginTop: space.lg,
          }}
        >
          <div style={{ ...typography.h3, color: '#fff' }}>Go VIP</div>
          <div style={{ ...typography.body, color: 'rgba(255,255,255,0.9)', lineHeight: 1.4 }}>
            Unlimited replies, see who liked you, unlock private photos.
          </div>
          <button
            onClick={handleSubscribe}
            data-testid="sidebar-vip-cta"
            style={{
              marginTop: space.xs,
              background: '#fff',
              color: colors.accent,
              border: 'none',
              borderRadius: radius.pill,
              padding: `${space.sm + 2}px ${space.lg}px`,
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            Upgrade
          </button>
        </div>
      )}
    </aside>
  );
});

export default DesktopSidebar;
